
import { useCallback } from "react";
import { Message } from "../message-item";
import { ConversationMessageService } from "../services/conversation-message-service";

type MessageFetcherProps = {
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setApiError: React.Dispatch<React.SetStateAction<string | null>>;
  setRetryCount: React.Dispatch<React.SetStateAction<number>>;
  retryCount: number;
  messages: Message[];
  onThreadIdChange: (threadId: string | null) => void;
  toast: any;
  WELCOME_MESSAGE: Message;
};

export const useChatMessageFetcher = ({
  setMessages,
  setIsLoading,
  setApiError,
  setRetryCount,
  retryCount,
  messages,
  onThreadIdChange,
  toast,
  WELCOME_MESSAGE
}: MessageFetcherProps) => {
  const maxRetries = 3;

  const fetchMessagesForThread = useCallback(async (threadId: string) => {
    try {
      setIsLoading(true);
      setApiError(null);
      
      console.log(`📥 [MESSAGE-FETCHER] Fetching messages for thread: ${threadId}`);
      
      // Load messages from database instead of OpenAI API
      const loadedMessages = await ConversationMessageService.loadMessages(threadId);
      
      if (loadedMessages.length > 0) {
        // Check if we already have a welcome message to avoid duplicates
        const hasWelcomeMessage = loadedMessages.some(msg => msg.isWelcomeMessage || msg.id === 'welcome-message');
        
        // Only add welcome message if not already present
        const finalMessages = hasWelcomeMessage ? loadedMessages : [WELCOME_MESSAGE, ...loadedMessages];
        
        setMessages(finalMessages);
        console.log(`✅ [MESSAGE-FETCHER] Successfully loaded ${loadedMessages.length} messages from database`);
      } else {
        // If no messages found, show only welcome message
        console.log("ℹ️ [MESSAGE-FETCHER] No messages found in database, showing welcome message");
        setMessages([WELCOME_MESSAGE]);
      }
      
      // Reset retry count on success
      setRetryCount(0);
    } catch (error: any) {
      console.error("❌ [MESSAGE-FETCHER] Error fetching messages from database:", error);
      
      setApiError(error.message || "Could not retrieve messages for this conversation.");
      toast({
        variant: "destructive",
        title: "Error retrieving messages",
        description: error.message || "Could not retrieve messages for this conversation."
      });
      
      // Don't reset messages if we already have messages
      if (messages.length <= 1) {
        setMessages([WELCOME_MESSAGE]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [setMessages, setIsLoading, setApiError, setRetryCount, messages.length, toast, WELCOME_MESSAGE]);

  return { fetchMessagesForThread };
};
